<template>
	<div class="organisation-payment-step">
		<b-overlay :show="saving" class="d-flex flex-column">
			<h4 v-if="hasSelectedPlan" class="mt-2">{{ $t("payment_overview") }}</h4>

			<b-row v-if="hasSelectedPlan" class="mt-2">
				<b-col cols="12" md="6">
					<b-list-group class="organisation-payment-lines">
						<b-list-group-item>
							<organisation-amount-total
								:amount-state="amountState"
								:currency-code="formConfig.currencyCode"
								:label="$t('total_to_pay')"
								@retry="$emit('retry')"
							>
								<div
									v-for="plan in amountPlans"
									:key="plan.organisationPlanId"
									class="organisation-payment-plan mb-2"
								>
									<div class="d-flex justify-content-between font-weight-bold">
										<span>{{ getPlanName(plan.organisationPlanId) }}</span>
										<span class="text-nowrap ml-2">
											{{ formatAmount(plan.amount) }}
										</span>
									</div>
									<div
										v-for="(line, index) in getPlanLines(plan.organisationPlanId)"
										:key="index"
										class="d-flex justify-content-between pl-3 small"
									>
										<span>
											<template v-if="line.quantity > 1">{{ line.quantity }} x </template>
											{{ getLineName(line) }}
										</span>
										<span class="text-nowrap ml-2">
											{{ formatAmount(line.amount) }}
										</span>
									</div>
								</div>
							</organisation-amount-total>
						</b-list-group-item>
					</b-list-group>
				</b-col>
			</b-row>

			<validation-observer v-slot="{ handleSubmit }">
				<b-row v-if="showPaymentChoice" class="mt-4">
					<b-col>
						<validation-provider
							v-slot="{ errors }"
							:rules="{ required: paymentRequired }"
							:name="$t('payment_choice')"
						>
							<b-form-group :label="$t('payment_choice')">
								<b-form-radio-group
									v-model="paymentChoice"
									name="organisation-payment-choice"
									stacked
								>
									<b-form-radio :value="paymentChoices.payNow">
										{{ $t("pay_now") }}
									</b-form-radio>
									<b-form-radio :value="paymentChoices.payLater">
										{{ $t("pay_later") }}
									</b-form-radio>
								</b-form-radio-group>
							</b-form-group>
							<p v-for="error in errors" :key="error" class="text-danger mb-0">
								{{ error }}
							</p>
						</validation-provider>
					</b-col>
				</b-row>

				<b-row v-if="isRecaptchaEnabled" class="mt-4">
					<b-col class="d-flex justify-content-center">
						<base-recaptcha
							:site-key="formConfig.recaptchaSiteKey"
							@verify="$emit('recaptcha-verify', $event)"
						/>
					</b-col>
				</b-row>

				<b-row>
					<b-col class="text-center mb-5">
						<hr />
						<b-btn
							variant="secondary"
							:disabled="saving"
							@click.stop.prevent="$emit('back')"
						>
							{{ $t("go_back") }}
						</b-btn>
						<b-btn
							:disabled="
								saving ||
									amountState.loading ||
									(isRecaptchaEnabled && !recaptchaToken)
							"
							class="mr-2 ml-2 confirm-button"
							variant="primary"
							@click.stop.prevent="handleSubmit(confirm)"
						>
							{{ willPayNow ? $t("confirm_and_pay") : $t("confirm") }}
						</b-btn>
					</b-col>
				</b-row>
			</validation-observer>
		</b-overlay>
	</div>
</template>

<script>
import { planMixin } from "@/shared";
import {
	ORGANISATION_PAYMENT_MODES,
	ORGANISATION_PAYMENT_CHOICES,
	getOrganisationAmountLines,
} from "@/services/organisationRegistrationFormApi";

// FOYS-27334: confirmation step for organisation forms with a payment mode
// (DirectPayment / PayNowOrLater). SendManually forms never show it.
export default {
	name: "OrganisationPaymentStep",
	mixins: [planMixin],
	props: {
		formConfig: {
			type: Object,
			required: true,
		},
		// { loading: boolean, error: boolean, result: object|null }
		amountState: {
			type: Object,
			required: true,
		},
		hasSelectedPlan: {
			type: Boolean,
			default: false,
		},
		// The selected plans as { id, name }.
		selectedPlans: {
			type: Array,
			default: () => [],
		},
		saving: {
			type: Boolean,
			default: false,
		},
		isRecaptchaEnabled: {
			type: Boolean,
			default: false,
		},
		recaptchaToken: {
			type: String,
			default: null,
		},
	},
	data() {
		return {
			paymentChoice: null,
			paymentChoices: ORGANISATION_PAYMENT_CHOICES,
		};
	},
	computed: {
		// One entry per selected plan, in selection order.
		amountPlans() {
			return (this.amountState.result && this.amountState.result.plans) || [];
		},
		paymentRequired() {
			return !!(this.amountState.result && this.amountState.result.paymentRequired);
		},
		showPaymentChoice() {
			// When the amount could not be loaded it is unknown whether payment is
			// required, so the choice is still offered but optional (no choice =
			// PayLater server-side; ignored when nothing has to be paid).
			return (
				this.formConfig.paymentMode === ORGANISATION_PAYMENT_MODES.payNowOrLater &&
				this.hasSelectedPlan &&
				(this.paymentRequired || this.amountState.error)
			);
		},
		willPayNow() {
			if (this.formConfig.paymentMode === ORGANISATION_PAYMENT_MODES.directPayment) {
				return this.paymentRequired;
			}

			return (
				this.showPaymentChoice &&
				this.paymentChoice === ORGANISATION_PAYMENT_CHOICES.payNow
			);
		},
		resultCurrencyCode() {
			return (
				(this.amountState.result && this.amountState.result.currencyCode) ||
				this.formConfig.currencyCode
			);
		},
	},
	mounted() {
		window.scrollTo({ top: 0, behavior: "smooth" });
	},
	methods: {
		getPlanName(organisationPlanId) {
			const plan = this.selectedPlans.find(
				(x) => String(x.id) === String(organisationPlanId)
			);
			if (plan) {
				return plan.name;
			}

			// Fall back to the plan's Membership line.
			const membershipLine = this.getPlanLines(organisationPlanId).find(
				(line) => line.kind === "Membership"
			);

			return membershipLine ? this.getLineName(membershipLine) : "";
		},
		// Fee and discount lines get a translated name; a remittance fee keeps
		// its own description when it has one.
		getLineName(line) {
			switch (line.kind) {
				case "Discount":
					return this.$t("discount");
				case "AdministrationFee":
					return this.$t("administration_fee");
				case "RemittanceFee":
					return line.name || this.$t("remittance_fee");
				default:
					return line.name;
			}
		},
		getPlanLines(organisationPlanId) {
			return getOrganisationAmountLines(this.amountState.result, organisationPlanId);
		},
		formatAmount(amount) {
			return this.formatOrganisationPlanPrice(amount || 0, this.resultCurrencyCode);
		},
		confirm() {
			this.$emit("confirm", this.showPaymentChoice ? this.paymentChoice : null);
		},
	},
};
</script>
